/**
 * X 공유 문구 템플릿.
 *
 * 상한이 빠듯하다(한글은 140자에서 X 상한에 걸린다). 그래서 이모지·머리말·
 * 해시태그로 자리를 쓰지 않고, 한 가지만 또렷하게 말하는 쪽으로 짠다.
 */

import { getPageScale } from './pageScale';
import { calculatePace } from './progressCalculator';
import { calculateBookProgress } from './bookUtils';
import { recordTimeMs, RECORD_TYPES } from './recordUtils';
import {
  truncateToWeight, MAX_TWEET_WEIGHT, tweetWeight,
  splitQuoteSegments, joinQuoteSegments, selectWithinBudget
} from './tweetUtils';
import { BOOK_STATUS, getBookStatus } from '../constants/bookStatus';

/** 남은 예산 안에서 인용을 자른다 */
const fitQuote = (text, suffix) => {
  const budget = MAX_TWEET_WEIGHT - tweetWeight(suffix);
  return truncateToWeight((text || '').trim(), budget);
};

const titleList = (books, max = 3) => {
  const names = books.slice(0, max).map(b => b.title);
  const rest = books.length - names.length;
  return names.join(', ') + (rest > 0 ? ` 외 ${rest}권` : '');
};

/** 그 날 그 책에서 읽은 쪽수 — 공유 문구도 사용자가 입력한 단위로 적는다 */
const pagesOn = (book, date) =>
  getPageScale(book).toDisplay(book?.readingHistory?.[date] || 0) ?? 0;

const pagesToday = (books, today) =>
  books.reduce((sum, b) => sum + pagesOn(b, today), 0);

/** 칩 라벨은 한 줄에 들어가야 한다 */
const shortTitle = (title = '', max = 9) =>
  title.length > max ? `${title.slice(0, max)}…` : title;

/** 페이스 칩이 목록을 뒤덮지 않게 */
const MAX_PACE_VARIANTS = 5;

/**
 * 각 템플릿은 { id, label, hint, build(ctx) } 형태.
 * build 가 null 을 돌려주면 지금 상황에 쓸 수 없는 템플릿이라 목록에서 뺀다.
 */
export const SHARE_TEMPLATES = [
  {
    id: 'quote',
    label: '오늘의 문장',
    hint: '오늘 남긴 인용 하나를 그대로',
    build: ({ records, bookById, today }) => {
      const todayQuotes = records
        .filter(r => r.type === RECORD_TYPES.QUOTE && r.date === today)
        .sort((a, b) => recordTimeMs(b) - recordTimeMs(a));
      const quote = todayQuotes[0]
        || records.filter(r => r.type === RECORD_TYPES.QUOTE)
             .sort((a, b) => recordTimeMs(b) - recordTimeMs(a))[0];
      if (!quote) return null;

      const book = bookById.get(quote.bookId);
      const page = book && quote.page
        ? `, p.${getPageScale(book).toDisplay(quote.page)}`
        : '';
      const suffix = book ? `\n— ${book.title}${page}` : '';

      // 긴 인용은 자동으로 자르지 않고 조각으로 넘긴다 — 어디를 남길지 직접 고르도록
      const budget = MAX_TWEET_WEIGHT - tweetWeight(suffix);
      const segments = splitQuoteSegments(quote.text, budget);
      const assemble = (ids) => `${joinQuoteSegments(segments, ids)}${suffix}`;
      const defaultSelected = selectWithinBudget(segments, budget);

      return {
        text: assemble(defaultSelected),
        segments,
        assemble,
        defaultSelected
      };
    }
  },
  {
    // '오늘의 독서'와 '연속 기록'을 한 장으로 합친 것.
    // 쪽수든 연속일이든 둘 중 하나만 있어도 쓸 수 있게 한다
    id: 'today',
    label: '오늘의 독서',
    hint: '오늘 읽은 쪽수와 연속 기록',
    build: ({ books, completedBooks, today, streak }) => {
      const all = [...books, ...completedBooks];
      const read = all.filter(b => (b.readingHistory?.[today] || 0) > 0);
      const pages = pagesToday(all, today);
      const days = streak?.currentStreak || 0;
      if (pages === 0 && days === 0) return null;

      // 책 제목은 줄을 따로 쓴다 — 서술문 끝에 붙이면 어미가 제목에 먹힌다
      const lines = [];
      if (pages > 0) {
        lines.push(`오늘 ${pages}쪽 읽음.`);
        if (read.length > 0) lines.push(titleList(read));
      }
      if (days > 0) {
        const total = streak.totalReadingDays > 0 ? ` 누적 ${streak.totalReadingDays}일.` : '';
        lines.push(`${days}일 연속으로 읽는 중.${total}`);
      }
      return lines.join('\n');
    }
  },
  {
    id: 'week',
    label: '주간 결산',
    hint: '최근 7일 요약',
    build: ({ books, completedBooks, today }) => {
      const all = [...books, ...completedBooks];
      const [y, m, d] = today.split('-').map(Number);
      let total = 0;
      let days = 0;
      const titles = new Set();

      for (let i = 0; i < 7; i += 1) {
        const cursor = new Date(Date.UTC(y, m - 1, d - i));
        const key = cursor.toISOString().split('T')[0];
        const dayTotal = all.reduce((sum, b) => {
          const p = pagesOn(b, key);
          if (p > 0) titles.add(b.title);
          return sum + p;
        }, 0);
        total += dayTotal;
        if (dayTotal > 0) days += 1;
      }
      if (total === 0) return null;

      const lines = [`최근 7일 동안 ${total}쪽 읽음. ${days}일 기록함.`];
      if (titles.size > 0) lines.push([...titles].slice(0, 3).join(', '));
      return lines.join('\n');
    }
  },
  {
    id: 'finished',
    label: '완독 보고',
    hint: '최근 완독한 책',
    build: ({ books }) => {
      const done = books
        .filter(b => getBookStatus(b) === BOOK_STATUS.COMPLETED && b.endDate)
        .sort((a, b) => (b.endDate || '').localeCompare(a.endDate || ''))[0];
      if (!done) return null;

      const lines = [`${done.title} 완독함.`];
      if (done.rating > 0) {
        lines[0] += ` ${'★'.repeat(done.rating)}${'☆'.repeat(5 - done.rating)}`;
      }
      if (done.oneLiner) lines.push(fitQuote(done.oneLiner, lines[0]));
      return lines.join('\n');
    }
  },
  {
    id: 'reading',
    label: '읽는 중',
    hint: '지금 읽고 있는 책과 진행률',
    build: ({ books }) => {
      const reading = books.filter(b => getBookStatus(b) === BOOK_STATUS.READING);
      if (reading.length === 0) return null;

      const lines = ['지금 읽는 중'];
      reading.slice(0, 4).forEach(b => {
        lines.push(`${b.title} ${calculateBookProgress(b)}%`);
      });
      return lines.join('\n');
    }
  },
  {
    // 페이스는 책마다 다르므로 한 권씩 칩으로 펼친다 — 어느 책을 올릴지 고를 수 있게
    id: 'pace',
    label: '페이스',
    hint: '완독까지 얼마나 남았는지',
    variants: ({ books }) => books
      .map(book => ({ book, pace: calculatePace(book) }))
      .filter(({ pace }) => pace.hasTarget && !pace.isDone && !pace.isOverdue)
      .slice(0, MAX_PACE_VARIANTS)
      .map(({ book, pace }) => {
        const scale = getPageScale(book);
        return {
          id: `pace:${book.id}`,
          label: `페이스 · ${shortTitle(book.title)}`,
          hint: `${book.title} 완독까지 얼마나 남았는지`,
          text: [
            `${book.title} ${calculateBookProgress(book)}%까지 옴.`,
            `완독까지 ${pace.daysLeft}일, 하루 ${scale.toDisplay(pace.pagesPerDay)}쪽씩 읽어야 함.`
          ].join('\n')
        };
      })
  }
];

/**
 * 지금 데이터로 만들 수 있는 템플릿만 추려서 문구까지 만들어 돌려준다.
 *
 * - `variants` 를 가진 항목은 여러 개(책별)로 펼쳐진다
 * - `build` 는 문자열, 또는 조각 선택이 붙은 객체를 돌려줄 수 있다
 */
export const buildAvailableTemplates = (ctx) =>
  SHARE_TEMPLATES.flatMap(t => {
    if (t.variants) return t.variants(ctx).filter(v => !!v.text);

    const built = t.build(ctx);
    if (!built) return [];

    const body = typeof built === 'string' ? { text: built } : built;
    return body.text ? [{ id: t.id, label: t.label, hint: t.hint, ...body }] : [];
  });
